@if (isOpen()) {
<div class="animate-fade-in fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
  (click)="close()">

  <!-- Card -->
  <div class="animate-scale-in w-full max-w-md rounded-2xl border border-border bg-card shadow-2xl overflow-hidden"
    (click)="$event.stopPropagation()">

    <!-- Header: tabs (or a back arrow + title when the info panel is open), info + close -->
    <div class="flex items-center justify-between px-5 pt-4">
      @if (showInfo()) {
      <button (click)="showInfo.set(false)" class="flex items-center gap-2 text-sm font-semibold text-foreground">
        <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
        </svg>
        How does it work?
      </button>
      } @else {
      <div class="flex items-center gap-4">
        <button (click)="setTab('invite')" class="text-sm font-semibold pb-1 border-b-2 transition-colors"
          [class.text-foreground]="tab() === 'invite'" [class.border-primary]="tab() === 'invite'"
          [class.text-muted-foreground]="tab() !== 'invite'" [class.border-transparent]="tab() !== 'invite'">
          Invite
        </button>
        <button (click)="setTab('rewards')" class="text-sm font-semibold pb-1 border-b-2 transition-colors"
          [class.text-foreground]="tab() === 'rewards'" [class.border-primary]="tab() === 'rewards'"
          [class.text-muted-foreground]="tab() !== 'rewards'" [class.border-transparent]="tab() !== 'rewards'">
          Rewards
        </button>
      </div>
      }
      <div class="flex items-center gap-1">
        @if (!showInfo()) {
        <button (click)="showInfo.set(true)" aria-label="How it works"
          class="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all">
          <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        </button>
        }
        <button (click)="close()" aria-label="Close"
          class="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all">
          <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>

    <!-- One body height for every state, so the card does not resize under the reader.
         Invite, Rewards, the info panel, the loading skeleton and the two error states all have
         different natural heights, and switching between them made the modal jump — the tabs
         themselves moved, so the control you had just clicked was no longer under the cursor.
         `min-h` holds it at the tallest tab's size; `max-h` + scroll keeps a long referral list
         inside the viewport rather than growing the card past it. -->
    <div class="p-5 min-h-[360px] max-h-[70vh] overflow-y-auto transition-opacity duration-150 ease-out motion-reduce:transition-none"
      [class.opacity-25]="bodySettling()">
      <!-- INFO PANEL — "How does it work?" -->
      @if (showInfo()) {
      <div class="space-y-5">
        <div>
          <h3 class="flex items-center gap-2 text-sm font-bold text-foreground">
            <span>🎉</span> How do our referrals work?
          </h3>
          <ul class="mt-2 space-y-1.5 text-sm text-muted-foreground list-disc pl-5">
            <li>Share HashBooks with your friends &amp; network via your link.</li>
            <li>You earn <strong class="text-foreground">20% of their subscription</strong> — up to <strong class="text-foreground">$500</strong> — when your referral becomes a paying customer.</li>
            <li>Your friends get a <strong class="text-foreground">20% discount</strong> on their first payment.</li>
          </ul>
        </div>
        <div>
          <h3 class="flex items-center gap-2 text-sm font-bold text-foreground">
            <span>🎁</span> How do I claim my reward?
          </h3>
          <ul class="mt-2 space-y-1.5 text-sm text-muted-foreground list-disc pl-5">
            <li>Your rewards appear in the <strong class="text-foreground">Rewards</strong> tab as your referrals subscribe.</li>
            <li>Add your payout details (securely via Stripe) to receive your rewards.</li>
            <li>Rewards are released <strong class="text-foreground">{{ referral()?.hold_days ?? 30 }} days</strong> after your referral's subscription, provided it's still active.</li>
          </ul>
        </div>
      </div>
      } @else if (loading()) {
      <div class="animate-pulse space-y-3">
        <div class="h-5 w-2/3 rounded bg-muted"></div>
        <div class="h-4 w-full rounded bg-muted"></div>
        <div class="h-14 rounded-xl bg-muted"></div>
        <div class="h-11 rounded-xl bg-muted"></div>
      </div>
      } @else if (error()) {
      <div class="text-center py-6">
        <p class="text-sm text-muted-foreground">We couldn't load your referral details.</p>
        <button (click)="load()" class="mt-3 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90">
          Try again
        </button>
      </div>
      } @else if (referral(); as ref) {

      <!-- INVITE TAB -->
      @if (tab() === 'invite') {
      <h2 class="text-lg font-bold text-foreground">Give 20% off. Get up to $500</h2>
      <p class="text-sm text-muted-foreground mt-1">
        Invite friends to HashBooks — they get <strong class="text-foreground">20% off</strong> their first payment, and you earn
        <strong class="text-foreground">20% of what they pay</strong>, up to $500 per referral.
      </p>

      <!-- Invite link -->
      <div class="mt-4 flex items-center gap-3 rounded-xl border border-border bg-background px-3 py-2.5">
        <svg class="w-5 h-5 shrink-0 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="20 12 20 22 4 22 4 12" /><rect x="2" y="7" width="20" height="5" /><line x1="12" y1="22" x2="12" y2="7" /><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" /><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
        </svg>
        <div class="min-w-0 flex-1">
          <p class="text-[11px] text-muted-foreground leading-tight">Invite link</p>
          <p class="text-sm text-foreground truncate">{{ ref.share_link }}</p>
        </div>
      </div>

      <button (click)="copy(ref.share_link, 'link')"
        class="mt-3 w-full py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">
        {{ copiedTarget() === 'link' ? 'Link copied' : 'Copy invite link' }}
      </button>

      <!-- Code -->
      <div class="flex items-center justify-center gap-2 mt-3">
        <span class="text-xs text-muted-foreground">Code:</span>
        <code class="px-2.5 py-1 rounded-lg bg-muted text-foreground text-sm font-mono tracking-wide">{{ ref.code }}</code>
        <button (click)="copy(ref.code, 'code')"
          class="text-xs font-semibold text-primary hover:underline inline-block text-center min-w-[3.5rem]">
          {{ copiedTarget() === 'code' ? 'Copied' : 'Copy' }}
        </button>
      </div>

      <!-- Share row -->
      <div class="flex items-center justify-center gap-3 mt-5">
        <a [href]="shareUrls().twitter" target="_blank" rel="noopener" aria-label="Share on X"
          class="w-10 h-10 rounded-full border border-border flex items-center justify-center text-foreground hover:bg-muted transition-colors">
          <svg class="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
        </a>
        <a [href]="shareUrls().email" aria-label="Share via email"
          class="w-10 h-10 rounded-full border border-border flex items-center justify-center text-foreground hover:bg-muted transition-colors">
          <svg class="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 5L2 7" /></svg>
        </a>
        <a [href]="shareUrls().telegram" target="_blank" rel="noopener" aria-label="Share on Telegram"
          class="w-10 h-10 rounded-full border border-border flex items-center justify-center text-foreground hover:bg-muted transition-colors">
          <svg class="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="currentColor"><path d="M21.944 4.667l-3.32 15.654c-.25 1.104-.906 1.378-1.837.858l-5.073-3.74-2.447 2.354c-.271.271-.498.498-1.02.498l.364-5.164 9.4-8.494c.409-.364-.089-.567-.634-.203L5.322 13.62l-5.006-1.567c-1.088-.34-1.108-1.088.227-1.61L20.54 3.06c.906-.34 1.7.203 1.404 1.607z"/></svg>
        </a>
        <a [href]="shareUrls().linkedin" target="_blank" rel="noopener" aria-label="Share on LinkedIn"
          class="w-10 h-10 rounded-full border border-border flex items-center justify-center text-foreground hover:bg-muted transition-colors">
          <svg class="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="currentColor"><path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM3 9h4v12H3zM9 9h3.8v1.64h.05c.53-1 1.83-2.05 3.76-2.05C20.4 8.59 21 11.09 21 14.34V21h-4v-5.9c0-1.4-.03-3.2-1.95-3.2-1.96 0-2.26 1.53-2.26 3.1V21H9z"/></svg>
        </a>
        <a [href]="shareUrls().whatsapp" target="_blank" rel="noopener" aria-label="Share on WhatsApp"
          class="w-10 h-10 rounded-full border border-border flex items-center justify-center text-foreground hover:bg-muted transition-colors">
          <svg class="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="currentColor"><path d="M17.5 14.4c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.49s1.07 2.89 1.22 3.09c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.19 1.87.12.57-.09 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.13-.27-.2-.57-.35zM12 2a10 10 0 0 0-8.6 15.06L2 22l5.06-1.33A10 10 0 1 0 12 2z"/></svg>
        </a>
      </div>
      }

      <!-- REWARDS TAB -->
      @if (tab() === 'rewards') {
      <h2 class="text-lg font-bold text-foreground">Your rewards</h2>
      <div class="grid grid-cols-2 gap-3 mt-4">
        <div class="rounded-xl border border-border bg-background p-3">
          <p class="text-xs text-muted-foreground">Total earned</p>
          <p class="text-lg font-bold text-emerald-600 mt-0.5">{{ ref.stats.total_earned_usd | currency:'USD' }}</p>
        </div>
        <div class="rounded-xl border border-border bg-background p-3">
          <p class="text-xs text-muted-foreground">Pending</p>
          <p class="text-lg font-bold text-foreground mt-0.5">{{ ref.stats.total_pending_usd | currency:'USD' }}</p>
        </div>
        <div class="rounded-xl border border-border bg-background p-3">
          <p class="text-xs text-muted-foreground">Ready to pay</p>
          <p class="text-lg font-bold text-foreground mt-0.5">{{ ref.stats.counts.ready_to_pay }}</p>
        </div>
        <div class="rounded-xl border border-border bg-background p-3">
          <p class="text-xs text-muted-foreground">Total referrals</p>
          <p class="text-lg font-bold text-foreground mt-0.5">{{ ref.stats.counts.total }}</p>
        </div>
      </div>

      @if (ref.stats.counts.total === 0) {
      <p class="text-sm text-muted-foreground mt-4 text-center">
        No referrals yet. When someone signs up via your link, they'll appear here. Rewards are released
        <strong class="text-foreground">{{ ref.hold_days }} day{{ ref.hold_days === 1 ? '' : 's' }}</strong>
        after their subscription, provided it's still active.
      </p>
      } @else {
      <p class="text-xs text-muted-foreground mt-4 text-center">
        Rewards are released {{ ref.hold_days }} day{{ ref.hold_days === 1 ? '' : 's' }} after a referred subscription, provided it's still active.
      </p>
      }

      <!-- Payout setup: state depends on whether the user has connected a payout account. -->
      @if (ref.payout.payouts_enabled) {
      <!-- Fully onboarded → nothing to do; show a connected state + a "Manage" link. -->
      <div class="mt-5 w-full py-2.5 rounded-xl bg-emerald-50 text-emerald-700 text-sm font-semibold flex items-center justify-center gap-2">
        <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
        Payouts connected
      </div>
      <button (click)="addPaymentDetails()" [disabled]="connecting()"
        class="text-[11px] text-muted-foreground hover:text-foreground text-center mt-2 w-full disabled:opacity-60">
        {{ connecting() ? 'Redirecting to Stripe…' : 'Manage payout details' }}
      </button>
      } @else {
      <!-- No account yet, or onboarding not finished → prompt to add / finish. -->
      <button (click)="addPaymentDetails()" [disabled]="connecting()"
        class="mt-5 w-full py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed">
        {{ connecting() ? 'Redirecting to Stripe…' : (ref.payout.connected ? 'Finish payout setup' : 'Add payment details') }}
      </button>
      <p class="text-[11px] text-muted-foreground text-center mt-2">
        Payouts are handled securely by Stripe. You'll be redirected to add your bank details.
      </p>
      }
      }

      } @else {
      <!-- Not loading, not an error, yet no referral (e.g. a 2xx with data: null) — show an
           explicit empty state with retry instead of a blank modal body. -->
      <div class="text-center py-6">
        <p class="text-sm text-muted-foreground">Referral details are unavailable right now.</p>
        <button (click)="load()" class="mt-3 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90">
          Try again
        </button>
      </div>
      }
    </div>
  </div>
</div>
}
